import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { CostOverview } from '../../types';
import { money, monthLabel } from '../../utils/costs';
import { CostPanel, Figure, Figures } from './CostPanel';

const TAB_OF: Record<string, string> = { aws: 'aws', database: 'database', ai: 'ai', services: 'services' };

/** This month's cost from every source, added up, with what could not be read named. */
const CostSummary: React.FC<{ overview: CostOverview }> = ({ overview }) => {
  const resumes = overview.ai.tailored_resumes;
  const forecast = overview.aws.available ? overview.aws.forecast_rest_of_month : null;
  return (
    <>
      <CostPanel
        title={`${monthLabel(overview.month)} so far`}
        description="What running JobFlo has cost this month, from each place a cost comes from."
      >
        <Figures label="This month's totals">
          <Figure
            label={overview.complete ? 'Total so far' : 'Total of what is known'}
            value={money(overview.total_known)}
            hint={overview.complete ? undefined : 'Some sources could not be read; see below'}
          />
          <Figure
            label="AWS still expected this month"
            value={forecast === null ? 'Not known yet' : money(forecast)}
            hint={forecast === null ? 'AWS needs more history to forecast' : 'AWS forecast for the rest of the month'}
          />
          <Figure label="Tailored resumes built" value={resumes.generated.this_month.toLocaleString('en-US')} hint="this month" />
          <Figure
            label="Cost per tailored resume"
            value={resumes.cost_per_resume === null ? 'None built yet' : money(resumes.cost_per_resume)}
            hint="model cost, average of all built"
          />
        </Figures>
      </CostPanel>

      <CostPanel title="By source">
        <TableContainer>
          <Table size="small" aria-label="Cost by source this month">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell align="right">This month</TableCell>
                <TableCell>How it is known</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.parts.map((part) => (
                <TableRow key={part.key}>
                  <TableCell>
                    <Link component={RouterLink} to={`/admin/costs/${TAB_OF[part.key]}`}>
                      {part.label}
                    </Link>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {part.amount === null ? 'Not known' : money(part.amount)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {part.note}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!overview.complete && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            The total leaves out every source marked "Not known", so the real figure is higher.
          </Alert>
        )}
      </CostPanel>
    </>
  );
};

export default CostSummary;
